import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import clearForm from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/ClearForm";
import {
    getSelectedCategoryPath,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { BrandModel, VariantDisplayTypeValues } from "@insite/client-framework/Types/ApiModels";
import { ProductAttributesStyles } from "@insite/content-library/Components/ProductAttributes";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductListVariantSwatch from "@insite/content-library/Widgets/ProductList/ProductListVariantSwatch";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomContactUsForm from "@tCooneyBrothers/Components/CustomContactUsForm";
import Prop65Component from "@tCooneyBrothers/Components/Prop65Component";
import RandomLengthComponent from "@tCooneyBrothers/Components/RandomLengthComponent";
import { ProductAvailabilityStyles } from "@tCooneyBrothers/Overrides/Components/ProductAvailability";
import ProductListActions from "@tCooneyBrothers/Overrides/Widgets/ProductList/ProductListActions";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showAvailability: boolean;
    showAttributes: boolean;
    showPrice?: boolean;
    showAddToList?: boolean;
    showImageBadges?: boolean;
    maxImageBadges?: number;
    showTextBadges?: boolean;
    maxTextBadges?: number;
    onClick?: React.EventHandler<React.MouseEvent>;
}

type DataLayerItem = {
    item_id: string;
    item_name: string;
    index: number;
    item_brand: string | BrandModel;
    item_category: string;
    item_category2?: string;
    item_category3?: string;
    item_category4?: string;
    item_category5?: string;
    item_list_id?: string;
    item_list_name?: string;
    price: number | undefined;
    quantity: number | undefined;
};

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const selectedCategoryPath = getSelectedCategoryPath(state);
    const pathnameLowerCase = location.pathname.toLowerCase();
    const path =
        selectedCategoryPath ||
        (pathnameLowerCase.startsWith("/content/") || pathnameLowerCase.startsWith("/.spire/")
            ? ""
            : location.pathname);
    const catalogPage = getCatalogPageStateByPath(state, path).value;
    return {
        settingsCollection: getSettingsCollection(state),
        category: getCategoryState(state, catalogPage?.categoryIdWithBrandId ?? catalogPage?.categoryId).value,
        productListCatalogPage: getCatalogPageStateByPath(state, path).value,
    };
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasProductContext;

export interface ProductListProductInformationStyles {
    gridContainer?: GridContainerProps;
    container?: InjectableCss;
    brandStyles?: ProductBrandStyles;
    descriptionStyles?: ProductDescriptionStyles;
    brandGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    partNumbersGridItem?: GridItemProps;
    swatchGridItem?: GridItemProps;
    imageBadgeGridItem?: GridItemProps;
    textBadgeGridItem?: GridItemProps;
    partNumbersStyles?: ProductPartNumbersStyles;
    availabilityAttrGridItem?: GridItemProps;
    availabilityGridItem?: GridItemProps;
    attributePropGridItem?: GridItemProps;
    availabilityStyles?: ProductAvailabilityStyles;
    prop65?: InjectableCss;
    attributesGridItem?: GridItemProps;
    attributes?: ProductAttributesStyles;
    attributeAddToList?: GridContainerProps;
    actionsGridItem?: GridItemProps;
    actionsListGridItem?: GridItemProps;
    halfGridItem?: GridItemProps;
    searchScoreDataGridItem?: GridItemProps;
    productInfoGridContainer?: GridContainerProps;
    viewReplacementHalfGridItem?: GridItemProps;
    viewReplacementContainer?: GridContainerProps;
    viewReplacementItem?: GridItemProps;
    viewReplacementBtn?: ButtonPresentationProps;
    discontinuedText?: TypographyPresentationProps;
    replacementProductLink?: LinkPresentationProps;
    discontiunedBadgeGridItem?: GridItemProps;
    discontinuedWrapper?: GridContainerProps;
    rfqItem?: GridItemProps;
    rfqWrapper?: GridContainerProps;
    rfqTextItem?: GridItemProps;
    rfqButtonItem?: GridItemProps;
    replacementButtonItem?: GridItemProps;
    rfqButton?: InjectableCss;
    rfqText?: TypographyPresentationProps;
    successModal?: ModalPresentationProps;
    modalContainer?: GridContainerProps;
    messageTextGridItem?: GridItemProps;
}

export const productInformationStyles: ProductListProductInformationStyles = {
    container: {
        css: css`
            width: 100%;
        `,
    },
    gridContainer: {
        gap: 0,
        css: css`
            width: 100%;
            background-color: #fafafa;

            [data-test-selector="actionAddToList"] {
                display: none;
            }
        `,
    },
    descriptionStyles: {
        productDetailLink: {
            css: css`
                width: 100%;
            `,
            typographyProps: {
                size: 20,
            },
        },
    },
    brandGridItem: {
        width: 12,
        css: css`
            padding: 5px 15px 5px 15px;
        `,
    },
    descriptionGridItem: {
        width: 12,
        css: css`
            padding: 5px 15px 5px 20px;
        `,
    },
    partNumbersGridItem: {
        width: 12,
        css: css`
            padding: 5px 0 0 20px;
        `,
    },
    availabilityAttrGridItem: {
        width: 12,
        css: css`
            padding: 5px 15px 5px 15px;
            padding-top: 0px;
            [class^="GridOffset"] {
                margin-top: 0;
            }
        `,
    },
    prop65: {
        css: css`
            margin-left: auto;
            align-items: center;
        `,
    },
    attributePropGridItem: {
        width: [6, 6, 12, 6, 5],
        css: css`
            padding: 15px 0;
            align-items: center;
            span,
            button {
                font-size: 13px;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            padding: 13px;
                            span,
                            button {
                                font-size: 11px;
                            }
                        `,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    availabilityGridItem: {
        width: [6, 6, 12, 6, 7],
        css: css`
            align-items: center;
            padding-right: 0;
            [data-test-selector="callForAvailability"] {
                font-size: 13px;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            // padding-bottom: 0;
                            [data-test-selector="callForAvailability"] {
                                font-size: 11px;
                            }
                        `,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    attributesGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding: 5px 15px 15px;
            flex-direction: column;
            > div:first-child {
                > div[class^="sc"] {
                    margin-top: -5px;
                }
            }
        `,
    },

    searchScoreDataGridItem: {
        width: 12,
    },

    actionsGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            align-items: flex-end;
        `,
    },

    actionsListGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            padding-top: 15px;
            [data-test-selector="actionAddToList"] {
                padding: 0;
                span {
                    text-transform: uppercase;
                }
            }
            section {
                display: none;
                ~ [class^="sc"] {
                    display: none;
                }
                ~ [data-test-selector="actionAddToList"] {
                    display: flex !important;
                }
            }
        `,
    },

    halfGridItem: {
        width: [12, 12, 12, 7, 7],
        css: css`
            padding-bottom: 0;
            [class^="GridOffset"] {
                margin-bottom: 0;
            }
        `,
    },
    viewReplacementHalfGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            padding-bottom: 0;
            [class^="GridOffset"] {
                margin-bottom: 0;
            }
        `,
    },

    attributeAddToList: {
        css: css`
            margin-top: 15px;
            .priceContainer {
                display: none;

                + div {
                    display: none;
                }
            }
        `,
    },
    swatchGridItem: {
        width: 12,
    },
    imageBadgeGridItem: {
        width: 12,
    },
    textBadgeGridItem: {
        width: 12,
    },
    productInfoGridContainer: {
        gap: 0,
    },
    viewReplacementContainer: {
        gap: 0,
    },
    viewReplacementItem: {
        width: 12,
    },
    viewReplacementBtn: {
        css: css`
            cursor: pointer;
            font-family: inherit;
            min-height: 40px;
            line-height: 1;
            backface-visibility: hidden;
            word-break: keep-all;
            padding: 0px 21px;
            background: rgb(88, 180, 108);
            border: 2px solid rgb(88, 180, 108);
            color: #141414;
            width: 100%;
            margin: 0 auto;
            &:hover {
                background: rgb(88, 180, 108);
                border: 2px solid rgb(88, 180, 108);
            }
        `,
    },
    discontinuedText: {
        css: css`
            font-size: 16px;
            font-weight: 600;
            padding-bottom: 10px;
            color: #3674ca;
        `,
    },
    replacementProductLink: {
        css: css`
            width: 60%;
            margin: 0 auto;
        `,
        typographyProps: {
            css: css`
                width: 100%;
                ${wrapInContainerStyles}
            `,
        },
    },
    discontiunedBadgeGridItem: {
        width: 6,
        css: css`
            padding: 5px 15px 5px 20px;
        `,
    },
    discontinuedWrapper: {
        offsetCss: css`
            flex-direction: row;
        `,
    },
    rfqItem: {
        width: 5,
    },
    rfqWrapper: {
        offsetCss: css`
            flex-direction: column;
        `,
    },
    rfqTextItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    rfqText: {
        weight: "bold",
        size: 15,
        css: css`
            color: #2450a8;
        `,
    },
    rfqButtonItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
            padding-top: 0;
        `,
    },
    replacementButtonItem: {
        width: 12,
        css: css`
            justify-content: center;
            padding-top: 0;
        `,
    },
    rfqButton: {
        css: css`
            cursor: pointer;
            background: rgb(88, 180, 108);
            border: 2px solid rgb(88, 180, 108);
            color: #141414;
            padding: 0 25px;
            &:hover {
                background: rgb(88, 180, 108);
                border: 2px solid rgb(88, 180, 108);
            }
        `,
    },
    successModal: {
        sizeVariant: "small",
        cssOverrides: {
            modalTitle: css`
                padding: 10px 20px;
                border-bottom: 3px solid #ef3742;
                background: #fafafa;
            `,
            modalContent: css`
                padding: 30px;
                padding-top: 0;
            `,
            modalBody: css`
                max-height: calc(100% - 40px) !important;
            `,
        },
    },
    messageTextGridItem: {
        width: 12,
        css: css`
            padding-top: 25px;
            padding-bottom: 0;
        `,
    },
};

const styles = productInformationStyles;

const ProductListProductInformation = ({
    productContext,
    settingsCollection,
    showBrand,
    showTitle,
    showPartNumbers,
    showAvailability,
    showAttributes,
    showPrice,
    showAddToList,
    showImageBadges,
    maxImageBadges,
    showTextBadges,
    maxTextBadges,
    category,
    productListCatalogPage,
    onClick,
}: Props) => {
    const { product } = productContext;
    if (!product) {
        return null;
    }
    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });
    const showDiscontinuedTag = product.properties?.discontinuedProduct?.toLowerCase() === "true" || false;
    const donotDisplayPrice = product.properties?.donotDisplayPrice?.toLowerCase() === "true" || false;
    const requestForReplacement = product.properties?.requestForReplacement?.toLowerCase() === "true" || false;
    let variantDisplayTrait = null;
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    if (product.variantTraits && product.variantTraits.length > 0) {
        const swatchedViewList = [
            VariantDisplayTypeValues.SwatchDropdown,
            VariantDisplayTypeValues.SwatchList,
            VariantDisplayTypeValues.SwatchGrid,
        ];
        const sortedVaraintTraits = product.variantTraits.slice().sort((a, b) => a.sortOrder - b.sortOrder);
        variantDisplayTrait = sortedVaraintTraits.find(x => swatchedViewList.includes(x.displayType));
    }

    const verifyEndpoint = () => {
        const urlArray = window.location.href.split("/");
        const endpoint = urlArray[urlArray.length - 1];
        return endpoint.toLowerCase().includes("search?");
    };

    const addItemListId = () => {
        if (verifyEndpoint()) {
            return "Search";
        }
        return category?.id;
    };

    const addItemListName = () => {
        if (verifyEndpoint()) {
            return "Search";
        }
        return category?.shortDescription;
    };
    const generateItemsList = () => {
        const products = productContext.product;
        const itemsArr: DataLayerItem[] = [];
        if (products) {
            const categoryArr = productListCatalogPage?.uri?.split("?")[1]?.split("/") || [];
            const categories = categoryArr?.slice(1, categoryArr.length);
            const categorys = categories.length === 0 ? "Search" : category?.shortDescription;
            const categoryId = addItemListId();
            const categoryName = addItemListName();

            itemsArr.push({
                item_id: product.productNumber || "",
                item_name: product.productTitle || "",
                index: 0,
                item_brand: product.brand?.name || "",
                item_category: categorys || "",
                item_list_id: categoryId || "",
                item_list_name: categoryName || "",
                price: productContext.productInfo.pricing?.unitNetPrice,
                quantity: productContext.productInfo.inventory?.qtyOnHand || 1,
            });
        }
        return itemsArr;
    };

    const generateViewItemsList = () => {
        const products = productContext.product;
        const itemsArr: DataLayerItem[] = [];
        if (products) {
            const categoryArr = productListCatalogPage?.uri?.split("?")[1]?.split("/") || [];
            const categories = categoryArr?.slice(1, categoryArr.length);
            const categorys = categories.length === 0 ? "Search" : category?.shortDescription;
            const categoryId = addItemListId();
            const categoryName = addItemListName();

            itemsArr.push({
                item_id: product.productNumber || "",
                item_name: product.productTitle || "",
                index: 0,
                item_brand: product.brand?.name || "",
                item_category: categorys || "",
                item_list_id: categoryId || "",
                item_list_name: categoryName || "",
                price: productContext.productInfo.pricing?.unitNetPrice,
                quantity: productContext.productInfo.inventory?.qtyOnHand || 1,
            });
        }
        return itemsArr;
    };

    const addToDataLayer = () => {
        const selectedItems = generateItemsList();
        const viewSelectedItems = generateViewItemsList();
        const dataLayer = (window as any).dataLayer || [];
        if (dataLayer) {
            dataLayer.push({ ecommerce: null });
            dataLayer.push({
                event: "select_item",
                ecommerce: {
                    selected_item_list_id: addItemListId(),
                    selected_item_list_name: addItemListName(),
                    selectedItems,
                },
            });
        }
    };
    const onButtonClickHandler = () => {
        setIsModalOpen(true);
    };
    const continueButtonClickHandlerToParent = (modalState: boolean) => {
        modalState = false;
        setIsModalOpen(modalState);
        clearForm();
    };
    const contactUsModalCloseHandler = () => setIsModalOpen(false);
    const replacementProductPath = product.properties?.replacementProductURL;
    const availability = productContext.productInfo?.inventory?.inventoryAvailabilityDtos?.[0].availability;
    const quantity = productContext.productInfo?.qtyOrdered;
    const { productTitle } = product;
    const { productNumber } = product;
    const brandName = product.brand?.name;

    return (
        <StyledWrapper {...styles.container}>
            <GridContainer {...styles.gridContainer}>
                <GridItem {...styles.halfGridItem}>
                    <GridContainer {...styles.productInfoGridContainer}>
                        {product.brand && showBrand && (
                            <GridItem {...styles.brandGridItem}>
                                <ProductBrand brand={product.brand} extendedStyles={styles.brandStyles} />
                            </GridItem>
                        )}
                        {showTitle && (
                            <GridItem {...styles.descriptionGridItem}>
                                <ProductDescription
                                    onClick={() => {
                                        addToDataLayer();
                                    }}
                                    product={productContext}
                                    extendedStyles={styles.descriptionStyles}
                                />
                            </GridItem>
                        )}
                        {showPartNumbers && (
                            <GridItem {...styles.partNumbersGridItem}>
                                <ProductPartNumbers
                                    productNumber={product.productNumber}
                                    customerProductNumber={product.customerProductNumber}
                                    manufacturerItem={product.manufacturerItem}
                                    extendedStyles={styles.partNumbersStyles}
                                />
                            </GridItem>
                        )}
                        {variantDisplayTrait && (
                            <GridItem {...styles.swatchGridItem}>
                                <ProductListVariantSwatch product={productContext} variantTrait={variantDisplayTrait} />
                            </GridItem>
                        )}
                        {showTextBadges && textBadges && (
                            <GridItem {...styles.textBadgeGridItem}>
                                <ProductTextBadges badgeList={textBadges} maxBadges={maxTextBadges ?? 4} />{" "}
                            </GridItem>
                        )}
                        {showImageBadges && imageBadges && (
                            <GridItem {...styles.imageBadgeGridItem}>
                                <ProductImageBadges badgeList={imageBadges} maxBadges={maxImageBadges ?? 4} />
                            </GridItem>
                        )}
                    </GridContainer>
                </GridItem>
                {showDiscontinuedTag ? (
                    !product.detail?.replacementProductId ? (
                        requestForReplacement && (
                            <GridItem {...styles.viewReplacementHalfGridItem}>
                                <GridContainer {...styles.gridContainer}>
                                    <GridItem {...styles.viewReplacementItem}>
                                        <Typography {...styles.discontinuedText} as="h2">
                                            {translate("This item has been discontinued by the manufacturer")}
                                        </Typography>
                                    </GridItem>
                                    <GridItem {...styles.replacementButtonItem}>
                                        <Button {...styles.rfqButton} onClick={onButtonClickHandler}>
                                            {translate("Request Replacement")}
                                        </Button>
                                        <Modal
                                            headline={translate("Request Replacement")}
                                            contentLabel="Request Replacement"
                                            {...styles.successModal}
                                            isOpen={isModalOpen}
                                            handleClose={contactUsModalCloseHandler}
                                        >
                                            <GridContainer {...styles.modalContainer}>
                                                <GridItem {...styles.messageTextGridItem}>
                                                    <CustomContactUsForm
                                                        availability={availability}
                                                        parentModalState={continueButtonClickHandlerToParent}
                                                        productQty={quantity}
                                                        productTitle={productTitle}
                                                        productNumber={productNumber}
                                                        brandName={brandName}
                                                        isRequestReplacement={true}
                                                    />
                                                </GridItem>
                                            </GridContainer>
                                        </Modal>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                        )
                    ) : (
                        <GridItem {...styles.viewReplacementHalfGridItem}>
                            <GridContainer {...styles.gridContainer}>
                                <GridItem {...styles.viewReplacementItem}>
                                    <Typography {...styles.discontinuedText} as="h2">
                                        {translate("This item has been discontinued by the manufacturer")}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.viewReplacementItem}>
                                    <Link
                                        {...styles.replacementProductLink}
                                        href={replacementProductPath}
                                        onClick={onClick}
                                        data-test-selector="replacementProductLink"
                                    >
                                        <Button {...styles.viewReplacementBtn} id={`View-Replacement-${product.id}`}>
                                            {translate("View Replacment")}
                                        </Button>
                                    </Link>
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                    )
                ) : donotDisplayPrice ? (
                    <GridItem {...styles.rfqItem}>
                        <GridContainer {...styles.rfqWrapper}>
                            <GridItem {...styles.rfqTextItem}>
                                <Typography {...styles.rfqText}>
                                    {translate("Pricing Available Upon Request")}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.rfqButtonItem}>
                                <Button {...styles.rfqButton} onClick={onButtonClickHandler}>
                                    {translate("Request Quote")}
                                </Button>
                                <Modal
                                    headline={translate("Request Quote")}
                                    contentLabel="Request Quote"
                                    {...styles.successModal}
                                    isOpen={isModalOpen}
                                    handleClose={contactUsModalCloseHandler}
                                >
                                    <GridContainer {...styles.modalContainer}>
                                        <GridItem {...styles.messageTextGridItem}>
                                            <CustomContactUsForm
                                                availability={availability}
                                                parentModalState={continueButtonClickHandlerToParent}
                                                productQty={quantity}
                                                productTitle={productTitle}
                                                productNumber={productNumber}
                                                brandName={brandName}
                                                isRfq={true}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                </Modal>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                ) : showPrice ? (
                    <GridItem {...styles.actionsGridItem} data-test-selector="actionGrid">
                        <ProductListActions showPrice={showPrice} showAddToList={showAddToList} />
                    </GridItem>
                ) : null}
            </GridContainer>
            <GridContainer {...styles.attributeAddToList}>
                <GridItem {...styles.halfGridItem}>
                    <GridContainer>
                        {settingsCollection.productSettings.showInventoryAvailability && showAvailability && (
                            <GridItem {...styles.availabilityAttrGridItem} data-test-selector="availabilityAttribute">
                                <GridContainer {...styles.discontinuedWrapper}>
                                    {showDiscontinuedTag && (
                                        <GridItem {...styles.discontiunedBadgeGridItem}>
                                            <LazyImage src="/UserFiles/Images/discontinued-sm.png" />
                                        </GridItem>
                                    )}
                                    {!showDiscontinuedTag && showAvailability && (
                                        <GridItem {...styles.availabilityGridItem}>
                                            <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                                        </GridItem>
                                    )}
                                    {product.properties.prop65 && (
                                        <GridItem {...styles.attributePropGridItem}>
                                            <Prop65Component
                                                messageToDisplay={product.properties.prop65}
                                                {...styles.prop65}
                                            ></Prop65Component>
                                        </GridItem>
                                    )}
                                </GridContainer>
                            </GridItem>
                        )}
                    </GridContainer>
                </GridItem>
                {!showDiscontinuedTag && showAddToList && !donotDisplayPrice && (
                    <GridItem {...styles.actionsListGridItem} data-test-selector="actionGrid">
                        <ProductListActions showPrice={showPrice} showAddToList={showAddToList} />
                    </GridItem>
                )}
                {product.properties?.randomLengthItem?.toLowerCase() === "true" && (
                    <GridItem {...styles.attributesGridItem}>
                        <RandomLengthComponent displayRandomLength={true}></RandomLengthComponent>
                    </GridItem>
                )}
            </GridContainer>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductListProductInformation));
function getSelectedCategoryPath1(state: ApplicationState): any {
    throw new Error("Function not implemented.");
}
